// CHANGE-card staff pages: the early-access queue (/staff/card) and one request (/staff/card/:id). Staff read
// requests, export them, keep an internal note and mark one a duplicate or spam. There is no approve or decline:
// ernd doesn't evaluate requests before an issuer exists (research.md 7), and the applicant never sees a note, mark
// or flag. The queue is oldest sent first and never reordered by an answer. The EIN shows as "ending 6789" until
// "Show EIN", which is logged.
// CHANGE-070: the same page in the staff app's kit. Rows link to the company. Flags are labelled for what they are.
import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Section, Sources, Strip, Table, ago, errorText, useCursor, useKit, type Column } from '../staff/kit'
import { CompanyLink, Facts, when } from '../staff/common'
import { STATE } from './copy'
import { shownFor } from './Fields'
import type { AiMerchant, FieldDef, Person, StaffCardDetail, StaffCardRow } from './types'
import './card.css'

/** Spend choices from before the new list (founder, 2026-11-03), read in the new words. Same map as fields.LEGACY_SPEND. */
const OLD_SPEND: Record<string, string> = { openai: 'frontier', anthropic: 'frontier', superserve: 'ai_apps', other_ai: 'ai_apps',
  software: 'ai_apps', ads: 'other', travel: 'other' }

const KEY = ['staff', 'card']
const SENT = ['received', 'invited']
const MARK: Record<string, string> = { duplicate: 'Duplicate', spam: 'Spam' }
const FLAG: Record<string, string> = {
  dup_ein: "Another company's request has the same EIN",
  new_admin: 'Became an admin less than 14 days before sending',
  claimed: 'Admin through a claim',
  same_domain: 'Another company on ernd shares its email domain (maybe the same team twice)',
}

// The answers are written for the member, as "Your name". Staff read them about someone else. The member form keeps its words.
const STAFF_LABEL: Record<string, string> = {
  applicant_name: 'Name', applicant_title: 'Job title', authority: 'Can act for the company on financial matters', description: 'What the company does',
  spend_categories: 'What they would put on it', cards_needed: 'Cards needed', physical_card: 'Physical cards, or virtual only',
  annual_revenue: 'Gross revenue, last full fiscal year', representative_is_applicant: 'Applicant is the executive with the most control of finances',
}
const STAFF_STEP: Record<string, string> = { basics: 'Company' }
const opens = (r: StaffCardRow) => r.status !== 'withdrawn' && !r.own_company
const isEmail = (v: unknown): v is string => typeof v === 'string' && /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/.test(v)
/** An email a person can click. Anything else stays plain text. */
const Mail = ({ to }: { to: string }) => (isEmail(to) ? <a className="st-link" href={`mailto:${to}`} data-testid="staff-card-mailto">{to}</a> : <>{to}</>)

type Switch = { on: boolean; staff_setting: boolean | null; env: boolean; source: 'staff' | 'environment' }

export function StaffCardQueue() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ rows: StaffCardRow[]; enabled: boolean; switch: Switch }>('GET', '/api/staff/card'), refetchInterval: 60_000 })
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'vendors' ? 'vendors' : 'requests'
  const navigate = useNavigate()
  const rows = q.data?.rows ?? []
  const sent = rows.filter((r) => SENT.includes(r.status))
  const rest = rows.filter((r) => !SENT.includes(r.status))
  const open = (r: StaffCardRow) => { if (opens(r)) navigate(`/staff/card/${r.id}`) }
  const { cur } = useCursor(tab === 'requests' ? [...sent, ...rest] : [], (r) => r.id, open)
  if (q.isLoading) return <Busy what="Loading card requests" />
  if (q.error) return <Page title="Card requests"><Err error={q.error} /></Page>
  return (
    <Page title="Card requests" testid="staff-card"
      sub={<Sources>
        <Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} label="Requests" live limit={180} />
        <span className="st-fresh">Oldest first, never reordered. Nobody is approved or declined here</span>
      </Sources>}
      right={<><Link className="st-btn" to="/staff/card/funnel" data-testid="staff-card-funnel-link">Where people stop</Link>
        <a className="st-btn" href="/api/staff/card/export" data-testid="staff-card-export">Download CSV</a></>}>
      <Strip testid="staff-card-tabs" value={tab} onChange={(t) => setParams(t === 'vendors' ? { tab: t } : {}, { replace: true })}
        items={[{ key: 'requests', label: 'Requests', n: sent.length }, { key: 'vendors', label: 'AI vendors' }]} />
      {tab === 'vendors' ? <AiVendors /> : (
        <>
          <Section title="Sent" count={sent.length}>
            <CardTable rows={sent} empty="No requests yet." sent cur={cur} onOpen={open} />
          </Section>
          {rest.length > 0 ? (
            <Section title="Drafts and withdrawn" count={rest.length}>
              <CardTable rows={rest} cur={cur} onOpen={open} />
            </Section>
          ) : null}
          <SwitchPanel sw={q.data!.switch} />
        </>
      )}
    </Page>
  )
}

/** The AI vendors whose card spend will earn the AI rate at launch (pr/card/rewards.py). Nothing earns yet. */
function AiVendors() {
  const qc = useQueryClient()
  const kit = useKit()
  const key = [...KEY, 'ai-merchants']
  const q = useQuery({ queryKey: key, queryFn: () => api<{ rows: AiMerchant[]; rates: { ai_vendor: number; other: number }; planned: string }>('GET', '/api/staff/card/ai-merchants') })
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const add = useMutation({
    mutationFn: () => api('POST', '/api/staff/card/ai-merchants', { name, descriptors: desc.split(',').map((d) => d.trim()).filter(Boolean) }),
    onSuccess: () => { setName(''); setDesc(''); qc.invalidateQueries({ queryKey: key }) },
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  const toggle = useMutation({
    mutationFn: (m: AiMerchant) => api('POST', `/api/staff/card/ai-merchants/${m.id}/active`, { active: !m.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  if (!q.data) return q.error ? <Err error={q.error} /> : null
  return (
    <Section title="AI vendors for card points" count={q.data.rows.length} testid="staff-card-ai-vendors"
      right={<Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} live />}>
      <p className="st-note">
        Planned at launch: {q.data.rates.ai_vendor} points per $1 at these vendors, partners their own rate, {q.data.rates.other} on everything else.
        A charge matches when its merchant name holds a fragment. Nothing earns from a card yet.
      </p>
      <Table rows={q.data.rows} rowKey={(m) => m.id} rowAttrs={(m) => ({ 'data-testid': 'staff-card-ai-vendor', 'data-active': m.active })} columns={[
        { key: 'name', head: 'Vendor', primary: true, cell: (m) => <span className={m.active ? undefined : 'st-muted'}>{m.name}{m.active ? '' : ', off'}</span> },
        { key: 'frag', head: 'Fragments', cell: (m) => m.descriptors.join(', ') },
        { key: 'by', head: 'Added by', cell: (m) => m.added_by_email ?? 'Seed list' },
        { key: 'do', head: '', label: 'Do', cell: (m) => <Button tone="quiet" disabled={toggle.isPending} onClick={() => toggle.mutate(m)}>{m.active ? 'Turn off' : 'Turn on'}</Button> },
      ]} />
      <form className="st-filters" onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
        <label className="st-field">Vendor<input className="st-input" data-testid="staff-card-ai-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} /></label>
        <label className="st-field">Fragments, comma separated<input className="st-input" data-testid="staff-card-ai-descriptors" value={desc} placeholder="baseten, baseten inc" onChange={(e) => setDesc(e.target.value)} /></label>
        <Button type="submit" data-testid="staff-card-ai-add" disabled={!name.trim() || !desc.trim() || add.isPending}>Add vendor</Button>
      </form>
    </Section>
  )
}

/** Whether members can start requests. Staff's setting wins over CARD_APPLICATIONS_ENABLED; no redeploy needed.
 *  Every change needs a reason and writes an audit row. */
function SwitchPanel({ sw }: { sw: Switch }) {
  const qc = useQueryClient()
  const kit = useKit()
  const [reason, setReason] = useState('')
  const set = useMutation({
    mutationFn: (on: boolean | null) => api('PUT', '/api/staff/card/switch', { on, reason }),
    onSuccess: () => { setReason(''); qc.invalidateQueries({ queryKey: KEY }) },
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  const where = sw.source === 'staff' ? 'set here by staff' : `from the deploy setting, which is ${sw.env ? 'on' : 'off'}`
  const ok = reason.trim().length >= 3 && !set.isPending
  return (
    <Section title="New requests" testid="staff-card-switch">
      <div data-on={sw.on} className="st-stack">
        <p><span className="st-strong" data-testid="staff-card-switch-state">{sw.on ? 'Members can send requests' : "Members can't start requests"}</span> <span className="st-muted">{where}</span></p>
        <form className="st-filters" onSubmit={(e) => e.preventDefault()}>
          <input className="st-input st-pay-note" placeholder="Reason, logged" aria-label="Reason, logged" data-testid="staff-card-switch-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          {sw.on ? <Button data-testid="staff-card-switch-off" disabled={!ok} onClick={() => set.mutate(false)}>Turn off</Button>
            : <Button tone="primary" data-testid="staff-card-switch-on" disabled={!ok} onClick={() => set.mutate(true)}>Turn on</Button>}
          {sw.source === 'staff' ? <Button tone="quiet" data-testid="staff-card-switch-env" disabled={!ok} onClick={() => set.mutate(null)}>Use the deploy setting</Button> : null}
        </form>
      </div>
    </Section>
  )
}

function CardTable({ rows, empty, sent, cur, onOpen }: { rows: StaffCardRow[]; empty?: string; sent?: boolean; cur: string | null; onOpen: (r: StaffCardRow) => void }) {
  const columns: Column<StaffCardRow>[] = [
    { key: 'name', head: 'Request', primary: true, cell: (r) => (
      <>
        {!opens(r) ? (r.legal_name || r.company_name) : <Link className="st-link" to={`/staff/card/${r.id}`}>{r.legal_name || r.company_name}</Link>}
        {r.own_company ? <span className="st-muted">, your company</span> : null}
      </>
    ) },
    { key: 'company', head: 'Company', cell: (r) => <CompanyLink id={r.company_id} name={r.company_name} /> },
    { key: 'status', head: 'Status', cell: (r) => STATE[r.status] },
    sent
      ? { key: 'sent', head: 'Sent', nowrap: true, cell: (r) => (r.submitted_at ? when(r.submitted_at) : '') }
      : { key: 'upd', head: 'Last change', nowrap: true, cell: (r) => when(r.withdrawn_at ?? r.updated_at) },
    ...(sent ? [{ key: 'age', head: 'Waiting', num: true, cell: (r: StaffCardRow) => (r.submitted_at && Date.parse(r.submitted_at) <= Date.now() ? ago(r.submitted_at) : '') }] : []),
    { key: 'by', head: 'By', cell: (r) => r.submitted_by_email ?? '' },
    { key: 'mark', head: 'Mark', cell: (r) => (r.staff_flag ? MARK[r.staff_flag] : '') },
  ]
  return <Table rows={rows} columns={columns} rowKey={(r) => r.id} empty={empty} testid="staff-card-table" cur={cur} onOpen={onOpen}
    rowAttrs={(r) => ({ 'data-testid': 'staff-card-row', 'data-id': r.id, 'data-status': r.status })} />
}

function value(def: FieldDef, v: unknown): string {
  if (v === null || v === undefined || v === '') return ''
  if (def.kind === 'select') return def.options?.find((o) => o.value === v)?.label ?? String(v)
  if (def.kind === 'multiselect' && Array.isArray(v)) return [...new Set(v.map((x) => def.options?.find((o) => o.value === (OLD_SPEND[x] ?? x))?.label ?? x))].join(', ')
  if (def.kind === 'money') return typeof v === 'number' ? cents(v) : ''
  if (def.kind === 'percent') return `${v}%`
  if (def.kind === 'bool' || def.kind === 'yes_no') return v ? 'Yes' : 'No'
  if (def.kind === 'address' && typeof v === 'object') {
    const a = v as Record<string, string>
    return [a.line1, a.line2, `${a.city}, ${a.state} ${a.zip}`].filter(Boolean).join(', ')
  }
  return String(v)
}

const ACTION: Record<string, string> = {
  'card.start': 'Draft started', 'card.save': 'Saved', 'card.submit': 'Sent', 'card.reveal': 'EIN shown',
  'card.note': 'Note or mark', 'card.withdraw': 'Withdrawn', 'card.expire': 'Closed by retention',
  'card.purge_ein': 'EIN deleted by retention', 'card.invite': 'Invited', 'card.keep': 'Kept after the 18-month email',
  'card.expiry_notice': '18-month email sent',
}

export function StaffCardPage() {
  const { id } = useParams()
  const qc = useQueryClient()
  const kit = useKit()
  const q = useQuery({ queryKey: [...KEY, id], queryFn: () => api<StaffCardDetail>('GET', `/api/staff/card/${id}`) })
  const [taxId, setTaxId] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [mark, setMark] = useState<'' | 'duplicate' | 'spam' | null>(null)
  const done = () => qc.invalidateQueries({ queryKey: KEY })
  const reveal = useMutation({
    mutationFn: () => api<{ tax_id: string | null }>('POST', `/api/staff/card/${id}/reveal`),
    onSuccess: (r) => { setTaxId(r.tax_id); done() },
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  const save = useMutation({
    mutationFn: (b: { note: string; flag: string | null }) => api('POST', `/api/staff/card/${id}/note`, b),
    onSuccess: () => { setNote(null); setMark(null); done(); kit.toast('Note saved') },
    onError: (e) => kit.toast(errorText(e), { bad: true }),
  })
  if (q.isLoading) return <Busy />
  if (q.error) return <Page title="Card request"><Err error={q.error} /></Page>
  const a = q.data!
  const answers = a.answers ?? {}
  const noteText = note ?? a.staff_note ?? ''
  const markValue = mark ?? a.staff_flag ?? ''
  const title = String(answers.legal_name ?? a.company.name)
  return (
    <Page title={title} testid="staff-card-detail"
      sub={<Sources><Link className="st-link st-fresh" to="/staff/card">Card requests</Link><Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} live /></Sources>}>
      <div data-status={a.status} className="st-two">
        <Facts items={[
          ['Status', <span key="s" data-testid="staff-card-status">{STATE[a.status]}{a.staff_flag ? `, marked ${MARK[a.staff_flag].toLowerCase()}` : ''}</span>],
          ['Company', <span key="c"><CompanyLink id={a.company.id} name={a.company.name} />{a.company.status !== 'active' ? ` (${a.company.status})` : ''}, on ernd {a.signals.days_on_ernd} days, {a.signals.members} {a.signals.members === 1 ? 'member' : 'members'}</span>],
          ['Sent', a.submitted_at ? <span key="b">{when(a.submitted_at)} by <Mail to={a.submitted_by_email ?? ''} /></span> : 'Not yet'],
          ...(a.invited_by_email ? [['Invited by', <span key="i" data-testid="staff-card-invited-by">{a.invited_by_email}</span>] as [string, JSX.Element]] : []),
          ['Admins', <span key="m">{a.admins.map((m, i) => <span key={m}>{i ? ', ' : ''}<Mail to={m} /></span>)}</span>],
          ...(a.signals.other_companies.length ? [['Their other companies on ernd', a.signals.other_companies.join(', ')] as [string, string]] : []),
          ['Issuer', a.issuer === 'none' ? 'None yet' : a.issuer],
          ['AI receipts at draft start', a.prefill?.monthly_ai_spend_cents
            ? `About ${cents(a.prefill.monthly_ai_spend_cents)} a month over ${a.prefill.spend_months} months` : 'None counted'],
        ]} />
        {a.flags.length ? (
          <Section title="Flags" count={a.flags.length} testid="staff-card-flags">
            <ul className="st-ptn-problems">{a.flags.map((f) => <li key={f}>{FLAG[f] ?? f}</li>)}</ul>
            <p className="st-note">For staff only. Flags never decline, hide or reorder a request.</p>
          </Section>
        ) : <span />}
      </div>

      {a.fields.steps.filter((s) => s.fields.length).map((s) => (
        <Section key={s.key} title={STAFF_STEP[s.key] ?? s.title} testid={`staff-card-${s.key}`}>
          <dl className="st-facts st-card-answers">
            {s.fields.filter((f) => shownFor(f, answers)).map((f) => f.kind === 'people' ? (
              ((answers[f.key] as Person[] | undefined) ?? []).map((p, i) => [
                <dt key={`k${p.id}`}>Owner {i + 1}</dt>,
                <dd key={`v${p.id}`}>{(f.fields ?? []).filter((x) => p[x.key] !== undefined && p[x.key] !== null).map((x) => (
                  <span key={x.key} className="card-person-line">{x.label}: {value(x, p[x.key])}</span>
                ))}</dd>,
              ])
            ) : [
              <dt key={`k${f.key}`}>{STAFF_LABEL[f.key] ?? f.label}</dt>,
              <dd key={`v${f.key}`} data-testid={`staff-card-value-${f.key}`}>
                {f.sensitive ? (taxId ?? (a.masked_tax_id ? `Hidden, ${a.masked_tax_id}` : <span className="st-faint">–</span>)) : f.kind === 'email' && isEmail(answers[f.key]) ? <Mail to={answers[f.key] as string} /> : value(f, answers[f.key]) || <span className="st-faint">–</span>}
                {f.sensitive && a.masked_tax_id && !taxId ? (
                  <> <Button tone="quiet" data-testid="staff-card-reveal" disabled={reveal.isPending} onClick={() => reveal.mutate()}>Show EIN</Button> <span className="st-muted">logged</span></>
                ) : null}
              </dd>,
            ])}
          </dl>
        </Section>
      ))}

      {a.status !== 'withdrawn' ? (
        <Section title="Staff note" testid="staff-card-actions">
          <form className="st-stack st-card-note" data-testid="staff-card-note-form" onSubmit={(e) => { e.preventDefault(); save.mutate({ note: noteText, flag: markValue || null }) }}>
            <p className="st-note">Internal. The company never sees the note or the mark.</p>
            <label className="st-field">Mark
              <select className="st-select" data-testid="staff-card-mark" value={markValue} onChange={(e) => setMark(e.target.value as typeof markValue)}>
                <option value="">None</option>
                <option value="duplicate">Duplicate</option>
                <option value="spam">Spam</option>
              </select>
            </label>
            <label className="st-field">Note
              <textarea className="st-text" data-testid="staff-card-note" rows={3} maxLength={4000} value={noteText} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="st-row">
              <Button type="submit" tone="primary" data-testid="staff-card-save-note" disabled={save.isPending || (!!markValue && !noteText.trim())}>Save note</Button>
              {markValue && !noteText.trim() ? <span className="st-muted">Say why in the note</span> : null}
            </div>
          </form>
        </Section>
      ) : null}

      <Section title="History" count={a.history.length} testid="staff-card-history">
        <Table rows={a.history} rowKey={(h) => h.id} columns={[
          { key: 'at', head: 'When', nowrap: true, cell: (h) => when(h.at) },
          { key: 'what', head: 'What', primary: true, cell: (h) => ACTION[h.action] ?? h.action },
          { key: 'who', head: 'Who', cell: (h) => h.actor_email ?? h.actor_kind },
          { key: 'detail', head: 'Detail', cell: (h) => historyDetail(h.after, h.note) },
        ]} />
      </Section>
    </Page>
  )
}

function historyDetail(after: Record<string, unknown> | null, note: string | null): string {
  const parts: string[] = []
  if (after && typeof after.step === 'string' && after.step) parts.push(`step ${after.step}`)
  const changed = after?.changed as string[] | undefined
  if (changed?.includes('ein')) parts.push('EIN changed')
  if (after && 'staff_flag' in after) parts.push(after.staff_flag ? `marked ${after.staff_flag}` : 'no mark')
  if (after?.note_changed) parts.push('note changed')
  if (note) parts.push(`note: ${note}`)
  return parts.join('; ')
}

